import type { Event } from '@cocoonspace/sdk-js/types/event'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { bookingConfig } from '@cocoonspace/shared/config/booking-config'
import { useEmitter } from '@cocoonspace/shared/hooks/use-emitter.hook'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { Button } from '@cocoonspace/ui/components/button'
import { Dialog, DialogContent } from '@cocoonspace/ui/components/dialog'
import { useLingui } from '@lingui/react/macro'
import { HourglassIcon } from 'lucide-react'
import { usePathname, useRouter } from 'next/navigation'

import { expirationEmitter } from '~/domains/booking/emitters/expiration-dialog.emitter'
import { useLocale } from '~/hooks/use-locale.hook'
import { getLocalizedPath, stripLocaleFromPath } from '~/i18n/routing'

export const ExpirationDialog = ({ mode }: { mode: 'booking' | 'extras' }) => {
	const { t } = useLingui()
	const router = useRouter()
	const pathname = usePathname() ?? ''
	const locale = useLocale()
	const intervalRef = useRef<number | undefined>(undefined)
	const [event, setEvent] = useState<Event | null>(null)

	const [isOpen, setIsOpen] = useState(false)
	const [isExpired, setIsExpired] = useState(false)
	const [isDismissed, setIsDissmissed] = useState(false)

	const handleExpirationStart = useCallback(
		(event: Event) => {
			setEvent(event)

			window.clearInterval(intervalRef.current)

			const warningTimeout = dateTimeUtils(event.expires_at).subtract(
				bookingConfig.warningTimeout,
				'minutes',
			)

			intervalRef.current = window.setInterval(() => {
				const now = dateTimeUtils()

				if (now.isSame(event.expires_at) || now.isAfter(event.expires_at)) {
					setIsExpired(true)
					setIsDissmissed(false)
					setIsOpen(true)
				} else if (!isDismissed && now.isAfter(warningTimeout)) {
					setIsOpen(true)
				}
			}, 1000)
		},
		[isDismissed],
	)

	const handleExpirationReset = useCallback(() => {
		window.clearInterval(intervalRef.current)

		setIsOpen(false)
		setIsExpired(false)
		setIsDissmissed(false)
	}, [])

	useEffect(() => {
		return () => {
			window.clearInterval(intervalRef.current)
		}
	}, [])

	useEffect(() => {
		if (!stripLocaleFromPath(pathname).startsWith('/booking')) {
			handleExpirationReset()
		}
	}, [handleExpirationReset, pathname])

	useEmitter(
		expirationEmitter,
		{
			'expiration.reset': handleExpirationReset,
			'expiration.start': handleExpirationStart,
		},
		[handleExpirationStart, handleExpirationReset],
	)

	const trads = useMemo(() => {
		if (mode === 'booking') {
			if (isExpired) {
				return {
					title: t`Le délai a expiré !`,
					body: t`Le créneau selectionné ne vous est plus réservé.`,
					button: t`Choisir un nouveau créneau`,
				}
			}

			return {
				title: t`Il vous reste ${intervalRef.current} pour valider votre réservation`,
				body: t`Passé ce délai, le créneau sélectionné ne vous sera plus réservé`,
				button: t`Ok, c'est noté`,
			}
		}

		if (mode === 'extras') {
			if (isExpired) {
				return {
					title: t`Le délai a expiré !`,
					body: t`La modification n'a pas été enregistrée.`,
					button: t`Retour`,
				}
			}

			return {
				title: t`Il vous reste ${intervalRef.current} pour valider votre modification.`,
				body: t`Passé ce délai, la modification ne sera pas enregistrée.`,
				button: t`Ok, c'est noté`,
			}
		}
	}, [mode, t, isExpired])

	if (!event) return null

	return (
		<Dialog
			onOpenChange={(isOpen) => {
				if (!isExpired && !isOpen) {
					setIsDissmissed(true)
				}
			}}
			open={isOpen && !isDismissed}
		>
			<DialogContent
				className='max-w-[450px]'
				hideCloseBtn={isExpired}
			>
				<div className='flex flex-col items-center gap-8 p-4'>
					<div className='flex items-center gap-4'>
						<HourglassIcon className='text-primary !size-[50px]' />
						<span className='text-xl font-bold'>{trads?.title}</span>
					</div>

					<span className='text-center'>{trads?.body}</span>

					<Button
						className='w-full'
						onClick={() => {
							if (isExpired) {
								router.push(
									getLocalizedPath(
										`/${event.space?.zone}/${event.space?.slug}`,
										locale,
									),
								)
							} else {
								setIsDissmissed(true)
							}
						}}
					>
						{trads?.button}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	)
}
